import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { exportCourseToPDF, exportCourseToWord, exportToTeachable, exportToThinkific, exportCourseToPDFWhiteLabel } from '../services/exportService';
import Footer from '../components/Footer';
import ShareModal from '../components/ShareModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface Lesson {
  title: string;
  description: string;
  duration: number;
  contentType: string;
  keyConcepts?: string[];
  content?: {
    introduction?: string;
    mainContent?: string;
    keyTakeaways?: string[];
    exercises?: string[];
    videoScript?: string;
    resources?: string[];
  };
}

interface Module {
  title: string;
  description: string;
  objectives?: string[];
  lessons: Lesson[];
  quiz?: {
    questions: Array<{
      question: string;
      options: string[];
      correctAnswer: string;
      explanation: string;
    }>;
  };
}

interface Course {
  id: string;
  title: string;
  description: string;
  niche: string;
  targetAudience?: string;
  learningObjectives?: string[];
  status: string;
  createdAt: string;
  modules: Module[];
}

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { token, user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedModule, setExpandedModule] = useState<number | null>(0);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [currentProgress, setCurrentProgress] = useState('');
  const [generatingModuleIdx, setGeneratingModuleIdx] = useState<number | null>(null);
  const [generatingLessonIdx, setGeneratingLessonIdx] = useState<number | null>(null);
  const [generatingQuizModuleIdx, setGeneratingQuizModuleIdx] = useState<number | null>(null);

  // Team Collaboration
  const [showShareModal, setShowShareModal] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    fetchCourse();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.course) {
        setCourse(response.data.course);
      } else {
        toast.error('Course not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      // Fallback to getting all courses
      try {
        const response = await axios.get(`${API_URL}/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const foundCourse = response.data.courses.find((c: Course) => c.id === courseId);
        if (foundCourse) {
          setCourse(foundCourse);
        } else {
          toast.error('Course not found');
          navigate('/dashboard');
        }
      } catch (err) {
        toast.error('Failed to load course');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveCourse = async (updatedCourse: Course) => {
    try {
      await axios.put(
        `${API_URL}/courses/${courseId}`,
        updatedCourse,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving course:', error);
    }
  };

  const generateAllContent = async () => {
    if (!course) return;

    setGeneratingContent(true);
    let updatedCourse = { ...course };

    try {
      // Generate content for each lesson
      for (let moduleIdx = 0; moduleIdx < course.modules.length; moduleIdx++) {
        const module = course.modules[moduleIdx];
        setGeneratingModuleIdx(moduleIdx);

        for (let lessonIdx = 0; lessonIdx < module.lessons.length; lessonIdx++) {
          const lesson = module.lessons[lessonIdx];
          setGeneratingLessonIdx(lessonIdx);

          setCurrentProgress(`Generating content for "${lesson.title}"...`);

          try {
            const response = await axios.post(
              `${API_URL}/ai/generate-lesson`,
              {
                lessonTitle: lesson.title,
                moduleContext: module.title,
                courseContext: course.title
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            updatedCourse.modules[moduleIdx].lessons[lessonIdx].content = response.data.lessonContent;
          } catch (error) {
            console.error(`Failed to generate content for ${lesson.title}:`, error);
          }
        }
        setGeneratingLessonIdx(null);

        // Generate quiz for module
        setGeneratingQuizModuleIdx(moduleIdx);
        setCurrentProgress(`Generating quiz for "${module.title}"...`);
        try {
          const response = await axios.post(
            `${API_URL}/ai/generate-quiz`,
            {
              moduleTitle: module.title,
              moduleContent: module.description
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          updatedCourse.modules[moduleIdx].quiz = response.data.quiz;
        } catch (error) {
          console.error(`Failed to generate quiz for ${module.title}:`, error);
        }
        setGeneratingQuizModuleIdx(null);
      }
      setGeneratingModuleIdx(null);

      setCourse(updatedCourse);

      // Save the updated course to backend
      setCurrentProgress('Saving course...');
      await saveCourse(updatedCourse);

      // Refetch course to ensure UI is in sync
      await fetchCourse();

      setCurrentProgress('');
      toast.success('All content generated and saved successfully!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate all content');
    } finally {
      setGeneratingContent(false);
      setGeneratingModuleIdx(null);
      setGeneratingLessonIdx(null);
      setGeneratingQuizModuleIdx(null);
    }
  };

  const generateLessonContent = async (moduleIdx: number, lessonIdx: number) => {
    if (!course) return;

    const module = course.modules[moduleIdx];
    const lesson = module.lessons[lessonIdx];

    setCurrentProgress(`Generating content for "${lesson.title}"...`);

    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-lesson`,
        {
          lessonTitle: lesson.title,
          moduleContext: module.title,
          courseContext: course.title
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedCourse = { ...course };
      updatedCourse.modules[moduleIdx].lessons[lessonIdx].content = response.data.lessonContent;
      setCourse(updatedCourse);

      // Save to backend
      setCurrentProgress('Saving...');
      await saveCourse(updatedCourse);

      // Refetch to update UI counts
      await fetchCourse();

      setCurrentProgress('');
      toast.success('Lesson content generated and saved!');
    } catch (error) {
      console.error('Error generating lesson content:', error);
      toast.error('Failed to generate lesson content');
      setCurrentProgress('');
    }
  };

  const generateModuleQuiz = async (moduleIdx: number) => {
    if (!course) return;

    const module = course.modules[moduleIdx];
    setGeneratingQuiz(true);
    setCurrentProgress(`Generating quiz for "${module.title}"...`);

    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-quiz`,
        {
          moduleTitle: module.title,
          moduleContent: module.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedCourse = { ...course };
      updatedCourse.modules[moduleIdx].quiz = response.data.quiz;
      setCourse(updatedCourse);

      // Save to backend
      setCurrentProgress('Saving...');
      await saveCourse(updatedCourse);

      // Refetch to update UI counts
      await fetchCourse();

      setCurrentProgress('');
      toast.success('Quiz generated and saved!');
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast.error('Failed to generate quiz');
      setCurrentProgress('');
    } finally {
      setGeneratingQuiz(false);
    }
  };

  const togglePublishStatus = async () => {
    if (!course) return;

    const newStatus = course.status === 'published' ? 'draft' : 'published';
    const confirmMessage = newStatus === 'published'
      ? 'Are you sure you want to publish this course? It will be visible to students.'
      : 'Are you sure you want to unpublish this course? It will no longer be visible to students.';

    if (!window.confirm(confirmMessage)) return;

    try {
      await axios.patch(
        `${API_URL}/courses/${courseId}/publish`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCourse({ ...course, status: newStatus });
      toast.success(`Course ${newStatus} successfully!`);
    } catch (error) {
      console.error('Error changing course status:', error);
      toast.error('Failed to update course status');
    }
  };

  const deleteCourse = async () => {
    if (!course) return;

    if (!window.confirm(`Are you sure you want to delete "${course.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Course deleted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  // Team Collaboration: Fetch comments
  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/${courseId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (error: any) {
      console.error('Error fetching comments:', error);
      // Don't show error for 403 (no access yet)
      if (error.response?.status !== 403) {
        console.error('Failed to load comments');
      }
    }
  };

  // Team Collaboration: Add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setAddingComment(true);
    try {
      const response = await axios.post(
        `${API_URL}/courses/${courseId}/comments`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Comment added!');
      setNewComment('');
      await fetchComments();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast.error(error.response?.data?.error || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // Team Collaboration: Delete comment
  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Delete this comment?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Comment deleted');
      await fetchComments();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast.error(error.response?.data?.error || 'Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons.length, 0);
  const lessonsWithContent = course.modules.reduce(
    (sum, m) => sum + m.lessons.filter(l => l.content).length,
    0
  );
  const modulesWithQuizzes = course.modules.filter(m => m.quiz).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Course Header */}
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <h1 className="text-4xl font-bold">{course.title}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  course.status === 'published'
                    ? 'bg-green-400 text-green-900'
                    : 'bg-yellow-400 text-yellow-900'
                }`}>
                  {course.status}
                </span>
              </div>
              <p className="text-purple-100 text-lg mb-4">{course.description}</p>
              {course.targetAudience && (
                <p className="text-purple-200 mb-2">
                  🎯 <strong>Target Audience:</strong> {course.targetAudience}
                </p>
              )}
              {course.niche && (
                <p className="text-purple-200">
                  📚 <strong>Niche:</strong> {course.niche}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowShareModal(true)}
                className="bg-white bg-opacity-20 text-white border-2 border-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
              <button
                onClick={() => navigate(`/marketing/${courseId}`)}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition"
              >
                📧 Marketing Tools
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-6">
            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
              <span className="text-sm">📖 {course.modules.length} Modules</span>
            </div>
            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
              <span className="text-sm">📝 {totalLessons} Lessons</span>
            </div>
            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
              <span className="text-sm">✅ {lessonsWithContent}/{totalLessons} Content Generated</span>
            </div>
            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
              <span className="text-sm">📊 {modulesWithQuizzes}/{course.modules.length} Quizzes</span>
            </div>
          </div>
        </div>

        {/* Progress Alert */}
        {currentProgress && (
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6 flex items-center">
            <div className="inline-block w-6 h-6 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mr-3"></div>
            <span className="text-purple-700 font-semibold">{currentProgress}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={generateAllContent}
              disabled={generatingContent}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {generatingContent ? 'Generating All Content...' : '🚀 Generate ALL Content & Quizzes'}
            </button>
            <button
              onClick={togglePublishStatus}
              className={`px-6 py-3 rounded-lg font-semibold transition ${
                course.status === 'published'
                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {course.status === 'published' ? '📦 Unpublish Course' : '🚀 Publish Course'}
            </button>
            <button
              onClick={() => {
                exportCourseToPDF(course);
                toast.success('PDF exported successfully!');
              }}
              className="border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition"
            >
              📄 Export as PDF
            </button>
            <button
              onClick={() => {
                exportCourseToWord(course);
                toast.success('HTML exported! Open in browser or import to Google Docs');
              }}
              className="border-2 border-indigo-600 text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition"
            >
              🌐 Export as HTML
            </button>
            <button
              onClick={() => navigate(`/certificates/${courseId}`)}
              className="border-2 border-green-600 text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-green-50 transition"
            >
              🎓 Certificates
            </button>
            <button
              onClick={() => navigate(`/video-generator/${courseId}`)}
              className="border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition"
            >
              🎬 AI Videos
            </button>
            <button
              onClick={() => {
                exportToTeachable(course);
                toast.success('Teachable CSV exported!');
              }}
              className="border-2 border-orange-500 text-orange-500 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition"
            >
              📚 Teachable Export
            </button>
            <button
              onClick={() => {
                exportToThinkific(course);
                toast.success('Thinkific JSON exported!');
              }}
              className="border-2 border-teal-500 text-teal-500 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition"
            >
              🎓 Thinkific Export
            </button>
            <button
              onClick={() => {
                exportCourseToPDFWhiteLabel(course, { removeBranding: true });
                toast.success('White-label PDF exported!');
              }}
              className="border-2 border-gray-600 text-gray-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              📄 White-Label PDF
            </button>
            <button
              onClick={deleteCourse}
              className="border-2 border-red-500 text-red-500 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition"
            >
              🗑️ Delete Course
            </button>
          </div>
        </div>

        {/* Learning Objectives */}
        {course.learningObjectives && course.learningObjectives.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Learning Objectives</h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {course.learningObjectives.map((obj, idx) => (
                <li key={idx} className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{obj}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Modules */}
        <div className="space-y-6">
          {course.modules.map((module, moduleIdx) => (
            <div key={moduleIdx} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Module Header */}
              <div
                onClick={() => setExpandedModule(expandedModule === moduleIdx ? null : moduleIdx)}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 cursor-pointer hover:from-indigo-100 hover:to-purple-100 transition"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">
                      Module {moduleIdx + 1}: {module.title}
                    </h4>
                    <p className="text-gray-600 mb-3">{module.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-purple-600 font-semibold">
                        📝 {module.lessons.length} Lessons
                      </span>
                      <span className="text-green-600 font-semibold">
                        ✅ {module.lessons.filter(l => l.content).length} Generated
                      </span>
                      {module.quiz && (
                        <span className="text-blue-600 font-semibold">
                          📊 Quiz Ready
                        </span>
                      )}
                      {generatingQuizModuleIdx === moduleIdx && (
                        <span className="flex items-center gap-2 text-blue-600 font-semibold animate-pulse">
                          <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                          Generating Quiz...
                        </span>
                      )}
                      {generatingModuleIdx === moduleIdx && generatingLessonIdx !== null && (
                        <span className="flex items-center gap-2 text-purple-600 font-semibold animate-pulse">
                          <span className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
                          Generating Lessons...
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generateModuleQuiz(moduleIdx);
                    }}
                    disabled={generatingQuiz || !!module.quiz}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                  >
                    {module.quiz ? '✓ Quiz Created' : '📊 Generate Quiz'}
                  </button>
                </div>
              </div>

              {/* Module Content */}
              {expandedModule === moduleIdx && (
                <div className="p-6">
                  {/* Lessons */}
                  <div className="space-y-4 mb-6">
                    {module.lessons.map((lesson, lessonIdx) => (
                      <div key={lessonIdx} className="border-2 border-gray-200 rounded-lg p-4 hover:border-purple-300 transition">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="bg-purple-100 text-purple-700 font-bold text-sm px-3 py-1 rounded-full">
                                Lesson {lessonIdx + 1}
                              </span>
                              {lesson.content && (
                                <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                                  ✓ Content Ready
                                </span>
                              )}
                              {generatingModuleIdx === moduleIdx && generatingLessonIdx === lessonIdx && (
                                <span className="flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded-full animate-pulse">
                                  <span className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
                                  Generating...
                                </span>
                              )}
                            </div>
                            <h5 className="text-lg font-bold text-gray-900 mb-1">{lesson.title}</h5>
                            <p className="text-gray-600 text-sm mb-2">{lesson.description}</p>
                            <div className="flex gap-3 text-xs text-gray-500">
                              <span>⏱️ {lesson.duration} min</span>
                              <span>📄 {lesson.contentType}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => generateLessonContent(moduleIdx, lessonIdx)}
                            disabled={currentProgress !== '' || !!lesson.content}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {lesson.content ? '✓ Generated' : '✨ Generate Content'}
                          </button>
                        </div>

                        {/* Lesson Content Display */}
                        {lesson.content && (
                          <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-4">
                            {lesson.content.introduction && (
                              <div>
                                <h6 className="font-semibold text-gray-900 mb-2">Introduction:</h6>
                                <p className="text-gray-700 text-sm">{lesson.content.introduction}</p>
                              </div>
                            )}
                            {lesson.content.mainContent && (
                              <div>
                                <h6 className="font-semibold text-gray-900 mb-2">Main Content:</h6>
                                <p className="text-gray-700 text-sm whitespace-pre-wrap">{lesson.content.mainContent}</p>
                              </div>
                            )}
                            {lesson.content.keyTakeaways && lesson.content.keyTakeaways.length > 0 && (
                              <div>
                                <h6 className="font-semibold text-gray-900 mb-2">Key Takeaways:</h6>
                                <ul className="list-disc list-inside space-y-1">
                                  {lesson.content.keyTakeaways.map((takeaway, idx) => (
                                    <li key={idx} className="text-gray-700 text-sm">{takeaway}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {lesson.content.videoScript && (
                              <div>
                                <h6 className="font-semibold text-gray-900 mb-2">Video Script:</h6>
                                <p className="text-gray-700 text-sm whitespace-pre-wrap bg-white p-3 rounded border border-gray-200">
                                  {lesson.content.videoScript}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Quiz Display */}
                  {module.quiz && (
                    <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
                      <h5 className="text-xl font-bold text-blue-900 mb-4">📊 Module Quiz</h5>
                      <div className="space-y-4">
                        {module.quiz.questions.map((q, qIdx) => (
                          <div key={qIdx} className="bg-white rounded-lg p-4 border border-blue-200">
                            <p className="font-semibold text-gray-900 mb-3">
                              {qIdx + 1}. {q.question}
                            </p>
                            <div className="space-y-2 mb-3">
                              {q.options.map((option, oIdx) => (
                                <div
                                  key={oIdx}
                                  className={`p-2 rounded ${
                                    option.startsWith(q.correctAnswer)
                                      ? 'bg-green-100 border border-green-300'
                                      : 'bg-gray-50 border border-gray-200'
                                  }`}
                                >
                                  {option}
                                  {option.startsWith(q.correctAnswer) && (
                                    <span className="text-green-700 font-semibold ml-2">✓ Correct</span>
                                  )}
                                </div>
                              ))}
                            </div>
                            <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                              <strong>Explanation:</strong> {q.explanation}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Team Collaboration: Comments Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <svg className="w-7 h-7 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Comments ({comments.length})
          </h3>

          {/* Add Comment Form */}
          <div className="mb-6">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
            />
            <div className="mt-3 flex justify-end">
              <button
                onClick={handleAddComment}
                disabled={addingComment || !newComment.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingComment ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </div>

          {/* Comments List */}
          {comments.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-gray-600 text-lg">No comments yet</p>
              <p className="text-gray-500 text-sm mt-2">Be the first to add a comment!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map(comment => (
                <div key={comment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {comment.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-semibold text-gray-900">{comment.user_name}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleDateString()} at {new Date(comment.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                      </div>
                    </div>
                    {(comment.user_id === user?.id) && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition ml-2"
                        title="Delete comment"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        courseId={courseId || ''}
        courseTitle={course?.title || ''}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CourseDetail;
